import { useMemo } from 'react'
import {
  OfferStatus,
  OfferFilter,
  OfferOrderBy,
  OfferPricingType,
  OrderDirection,
  OfferType,
} from 'src/common/apollo/types/swarmx/graphql'
import { AnyToken } from 'src/common/types/models/Tokens'
import { denormalize, ZERO } from 'src/common/utils/big-utils'
import useOffers from 'src/common/apollo/hooks/useOffers'
import Big from 'big.js'
import dayjs from 'dayjs'
import useAuthorizations from 'src/common/queries/useAuthorizations/useAuthorizations'
import { getAuthAddressesFromOffers } from 'src/common/utils/offers'

export interface UseBestOffersParams {
  sellToken: AnyToken | undefined
  buyToken: AnyToken | undefined
  accountAddress: string | undefined
  sellTokenAmount?: Big
  buyTokenAmount?: Big
  mode?: number
}

const expiryTimestamp = dayjs().unix().toString()
const limit = 100

export const usePairOffers = ({
  sellToken,
  buyToken,
  sellTokenAmount,
  buyTokenAmount,
  mode = 0,
}: UseBestOffersParams) => {
  const skip =
    !buyToken || !sellToken || (sellTokenAmount?.eq(0) && buyTokenAmount?.eq(0))
  // const { smartAccountInfo } = useSmartAccount()

  const buildFilter = (
    type: OfferType,
    pricingType: OfferPricingType
  ): OfferFilter => ({
    withdrawalAsset_: {
      address: sellToken?.contractAddress?.toLowerCase() || '',
    },
    depositAsset_: {
      address: buyToken?.contractAddress?.toLowerCase() || '',
    },
    offerType: type,
    expiryTimestamp_gt: expiryTimestamp,
    offerStatus_not_in: [OfferStatus.Cancelled, OfferStatus.FullyTaken],
    offerPrice_: { pricingType },
    isPrivate: false,
    // ...(smartAccountInfo?.address && {
    //   maker_not_contains_nocase: smartAccountInfo.address,
    // }),
    id_not_in: ['0x6'],
    ...(type === OfferType.BlockOffer &&
      sellTokenAmount?.gt(0) &&
      mode === 0 && {
        amountOut_lte: denormalize(
          sellTokenAmount,
          sellToken?.decimals
        ).toString(),
      }),
    ...(type === OfferType.BlockOffer &&
      buyTokenAmount?.gt(0) &&
      mode === 1 && {
        amountIn_lte: denormalize(
          buyTokenAmount!,
          buyToken?.decimals
        ).toString(),
      }),
  })

  const { offers: fullFixedOffers, loading: loadingFullFixedOffers } =
    useOffers({
      variables: {
        filter: buildFilter(
          OfferType.BlockOffer,
          OfferPricingType.FixedPricing
        ),
        orderBy: OfferOrderBy.OfferPriceUnitPrice,
        orderDirection: OrderDirection.Desc,
        limit,
      },
      skip,
    })
  const { offers: partialFixedOffers, loading: loadingPartialFixedOffers } =
    useOffers({
      variables: {
        filter: buildFilter(
          OfferType.PartialOffer,
          OfferPricingType.FixedPricing
        ),
        orderBy: OfferOrderBy.OfferPriceUnitPrice,
        orderDirection: OrderDirection.Desc,
        limit,
      },
      skip,
    })
  const { offers: fullDynamicOffers, loading: loadingFullDynamicOffers } =
    useOffers({
      variables: {
        filter: buildFilter(
          OfferType.BlockOffer,
          OfferPricingType.DynamicPricing
        ),
        orderBy: OfferOrderBy.OfferPriceUnitPrice,
        orderDirection: OrderDirection.Desc,
        limit,
      },
      skip,
    })
  const { offers: partialDynamicOffers, loading: loadingPartialDynamicOffers } =
    useOffers({
      variables: {
        filter: buildFilter(
          OfferType.PartialOffer,
          OfferPricingType.DynamicPricing
        ),
        orderBy: OfferOrderBy.OfferPriceUnitPrice,
        orderDirection: OrderDirection.Desc,
        limit,
      },
      skip,
    })

  const offersWithUnitPrice = useMemo(() => {
    return [
      ...partialDynamicOffers,
      ...fullDynamicOffers,
      ...partialFixedOffers,
      ...fullFixedOffers,
    ].map(offer => ({
      ...offer,
      offerPrice: {
        ...offer.offerPrice,
        unitPrice: offer.amountOut!.div(offer.amountIn),
      },
    }))
  }, [
    partialDynamicOffers,
    fullDynamicOffers,
    partialFixedOffers,
    fullFixedOffers,
  ])

  const offers = useMemo(
    () =>
      offersWithUnitPrice.sort((a, b) => {
        return a.offerPrice.unitPrice?.gt(b.offerPrice.unitPrice || ZERO)
          ? 1
          : -1
      }),
    [offersWithUnitPrice]
  )

  const authAddresses = getAuthAddressesFromOffers(offers)

  const { checkOneOfAddressesIsAuthorized, isLoading: isAuthLoading } =
    useAuthorizations(authAddresses, {
      enabled: authAddresses.length > 0,
    })

  const offersWithAuth = useMemo(() => {
    return offers.map(offer => ({
      ...offer,
      isFullyAuthorized:
        offer.authorizationAddresses &&
        checkOneOfAddressesIsAuthorized(offer.authorizationAddresses),
    }))
  }, [checkOneOfAddressesIsAuthorized, offers])

  return {
    offers: offersWithAuth,
    loading:
      loadingFullFixedOffers ||
      loadingPartialFixedOffers ||
      loadingFullDynamicOffers ||
      loadingPartialDynamicOffers ||
      isAuthLoading,
  }
}
